import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
    Activity,
    Clock,
    AlertTriangle,
    RefreshCw,
    BarChart3,
    Target,
    Zap,
    Database,
    Cpu,
    Radar,
    Search,
    ChevronLeft,
    ChevronRight,
    Sparkles,
    Shield,
    ShieldAlert,
    ShieldCheck,
    Lock,
    Globe,
    Server,
    ExternalLink,
    Filter,
    X,
    Play,
    Pause,
    CheckCircle2,
    Ticket,
    Copy,
    ChevronDown,
    Layers,
    ArrowUpRight,
    Maximize2,
    Info,
    SlidersHorizontal,
    Code,
    Terminal,
    Radio
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { backendURL } from '../config';
import { analyzeLog } from '../utils/SmartLogAnalyzer';
import AbuseIPDBPopover from '../components/AbuseIPDBPopover';

// Curated dark SOC palette
const SEVERITY_COLORS = {
    critical: "#EF4444",
    error: "#F87171",
    warning: "#F59E0B",
    notice: "#38BDF8",
    info: "#10B981",
    low: "#60A5FA"
};

const ACTION_COLORS = {
    accept: "#10B981",
    pass: "#34D399",
    deny: "#EF4444",
    block: "#DC2626",
    blocked: "#DC2626",
    close: "#64748B",
    "client-rst": "#94A3B8",
    "server-rst": "#94A3B8",
    timeout: "#F59E0B",
    "ssl-alert": "#A855F7"
};

// ISO 2-letter Country Flags and Names
const COUNTRY_NAMES = {
    US: "United States",
    CA: "Canada",
    NL: "Netherlands",
    DE: "Germany",
    PK: "Pakistan",
    GB: "United Kingdom",
    IE: "Ireland",
    CN: "China",
    SG: "Singapore",
    FR: "France",
    IN: "India",
    AU: "Australia",
    BR: "Brazil",
    JP: "Japan"
};

const EventExplorer = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const urlSearch = searchParams.get("search") || searchParams.get("q") || searchParams.get("ip") || searchParams.get("srcip") || "";

    // --- Data States ---
    const [logs, setLogs] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // --- Stream & Controls ---
    const [isStreaming, setIsStreaming] = useState(true);
    const [refreshInterval, setRefreshInterval] = useState(5000); // 5s default
    const [lastSyncTime, setLastSyncTime] = useState(new Date());

    // --- Filter States ---
    const [searchQuery, setSearchQuery] = useState(urlSearch);
    const [debouncedSearch, setDebouncedSearch] = useState(urlSearch);
    const [selectedEngine, setSelectedEngine] = useState("all");
    const [selectedGateway, setSelectedGateway] = useState("all");
    const [selectedSubtype, setSelectedSubtype] = useState("all");
    const [selectedAction, setSelectedAction] = useState("all");
    const [selectedSeverity, setSelectedSeverity] = useState("all");
    const [timeRange, setTimeRange] = useState("all");

    // Sync from URL search params whenever they change
    useEffect(() => {
        const s = searchParams.get("search") || searchParams.get("q") || searchParams.get("ip") || searchParams.get("srcip") || "";
        if (s && s !== searchQuery) {
            setSearchQuery(s);
            setDebouncedSearch(s);
            setPage(1);
        }
    }, [searchParams]);

    // --- Pagination ---
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [totalLogs, setTotalLogs] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // --- Inspection Drawer & Actions ---
    const [selectedLog, setSelectedLog] = useState(null);
    const [drawerTab, setDrawerTab] = useState("overview"); // overview, attributes, raw
    const [copiedRaw, setCopiedRaw] = useState(false);
    const [stagingStatus, setStagingStatus] = useState({}); // { [logId]: 'staging' | 'staged' | 'error' }
    const [zohoStatus, setZohoStatus] = useState({}); // { [logId]: 'loading' | 'created' | 'error' }
    const [notification, setNotification] = useState(null);

    // Debounce search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(1);
        }, 350);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    // Show Notification Toast with optional navigation link
    const triggerToast = (msg, type = 'success', link = null, linkText = null) => {
        setNotification({ msg, type, link, linkText });
        setTimeout(() => setNotification(null), 5000);
    };

    // --- Fetch Real Aggregated Summary ---
    const fetchSummary = async () => {
        try {
            const res = await axios.get(`${backendURL}/api/soc/event-explorer/summary`);
            if (res.data) {
                setSummary(res.data);
                if (res.data.totalLogs && totalLogs === 0) {
                    setTotalLogs(res.data.totalLogs);
                }
            }
        } catch (err) {
            console.error("Failed to fetch event explorer summary:", err);
        }
    };

    // --- Fetch Real Live Logs ---
    const fetchLogs = async (silent = false) => {
        if (!silent) setIsRefreshing(true);
        try {
            const params = {
                page,
                limit,
                search: debouncedSearch,
                gateway: selectedGateway,
                subtype: selectedSubtype,
                action: selectedAction,
                severity: selectedSeverity,
                sourceType: selectedEngine !== "all" ? selectedEngine : undefined,
                timeRange
            };

            const res = await axios.get(`${backendURL}/api/soc/event-explorer/logs`, { params });
            if (res.data) {
                setLogs(res.data.logs || []);
                setTotalLogs(res.data.pagination?.total || 0);
                setTotalPages(res.data.pagination?.totalPages || 1);
                setLastSyncTime(new Date());
            }
        } catch (err) {
            console.error("Failed to fetch event explorer logs:", err);
        } finally {
            setLoading(false);
            if (!silent) setIsRefreshing(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchSummary();
    }, []);

    // Fetch on filter or page change
    useEffect(() => {
        fetchLogs();
    }, [page, limit, debouncedSearch, selectedEngine, selectedGateway, selectedSubtype, selectedAction, selectedSeverity, timeRange]);

    // Auto-refresh stream timer
    useEffect(() => {
        if (!isStreaming || refreshInterval === 0) return;
        const timer = setInterval(() => {
            fetchLogs(true);
            fetchSummary();
        }, refreshInterval);
        return () => clearInterval(timer);
    }, [isStreaming, refreshInterval, page, limit, debouncedSearch, selectedEngine, selectedGateway, selectedSubtype, selectedAction, selectedSeverity, timeRange]);

    // Active Filters list for chips
    const activeFilterChips = useMemo(() => {
        const chips = [];
        if (selectedEngine !== 'all') chips.push({ type: 'engine', label: `Vendor: ${selectedEngine.toUpperCase()}`, reset: () => setSelectedEngine('all') });
        if (selectedGateway !== 'all') chips.push({ type: 'gateway', label: `Gateway: ${selectedGateway}`, reset: () => setSelectedGateway('all') });
        if (selectedSubtype !== 'all') chips.push({ type: 'subtype', label: `Engine: ${selectedSubtype}`, reset: () => setSelectedSubtype('all') });
        if (selectedAction !== 'all') chips.push({ type: 'action', label: `Action: ${selectedAction.toUpperCase()}`, reset: () => setSelectedAction('all') });
        if (selectedSeverity !== 'all') chips.push({ type: 'severity', label: `Severity: ${selectedSeverity.toUpperCase()}`, reset: () => setSelectedSeverity('all') });
        if (timeRange !== 'all') chips.push({ type: 'timeRange', label: `Time: ${timeRange}`, reset: () => setTimeRange('all') });
        if (debouncedSearch) chips.push({ type: 'search', label: `Search: "${debouncedSearch}"`, reset: () => setSearchQuery('') });
        return chips;
    }, [selectedEngine, selectedGateway, selectedSubtype, selectedAction, selectedSeverity, timeRange, debouncedSearch]);

    const resetAllFilters = () => {
        setSelectedEngine('all');
        setSelectedGateway('all');
        setSelectedSubtype('all');
        setSelectedAction('all');
        setSelectedSeverity('all');
        setTimeRange('all');
        setSearchQuery('');
        setPage(1);
    };

    // --- Action: Stage Firewall Rule ---
    const handleStageFirewallBlock = async (e, log) => {
        if (e && e.stopPropagation) e.stopPropagation();
        const targetIp = log.srcip || log.sourceIp;
        if (!targetIp || targetIp === 'Internal' || targetIp.startsWith('127.')) {
            triggerToast("Cannot block internal loopback/host", "error");
            return;
        }

        const logId = log._id;
        setStagingStatus(prev => ({ ...prev, [logId]: 'staging' }));

        try {
            const token = localStorage.getItem("token");
            const gateway = log.devname || log.deviceName || "BCS-80-WAN-FW-01";
            const res = await axios.post(`${backendURL}/api/firewall/approvals`, {
                targetEntity: targetIp,
                targetGateway: gateway,
                actionType: "BLOCK_IP",
                incidentId: `INC-EVT-${String(log._id).slice(-4)}`,
                incidentName: `Perimeter Auto-Defense: Block ${targetIp} (${log.subtype || 'Syslog'})`,
                fortiosCli: `config firewall address\n  edit "BLOCK_${targetIp}"\n  set subnet ${targetIp}/32\n  set comment "QueryTel SOC Event Explorer Block Request - Staged for Admin Review"\nend\nconfig firewall addrgrp\n  edit "SOC_DYNAMIC_BLOCKLIST"\n  append member "BLOCK_${targetIp}"\nend`
            }, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            if (res.data?.success) {
                setStagingStatus(prev => ({ ...prev, [logId]: 'staged' }));
                triggerToast(
                    `🛡️ Staged Firewall Block for ${targetIp} on ${gateway}!`,
                    "success",
                    "/firewall-approvals",
                    "View in Approvals →"
                );
            } else {
                throw new Error(res.data?.error || "Failed");
            }
        } catch (err) {
            console.error("Failed to stage firewall rule:", err);
            setStagingStatus(prev => ({ ...prev, [logId]: 'error' }));
            triggerToast(`Failed to stage block for ${targetIp}`, "error");
        }
    };

    // --- Action: Create Zoho Desk Ticket ---
    const handleCreateZohoTicket = async (e, log) => {
        if (e && e.stopPropagation) e.stopPropagation();
        const logId = log._id;
        setZohoStatus(prev => ({ ...prev, [logId]: 'loading' }));

        try {
            const token = localStorage.getItem("token");
            const narrative = analyzeLog(log);
            const description = `
QueryTel SOC Telemetry Incident Report
--------------------------------------
Log ID: ${log._id}
Device / Gateway: ${log.devname || log.deviceName || 'N/A'}
Timestamp: ${new Date(log.ts || log.timestamp || Date.now()).toISOString()}
Severity: ${(log.severity || log.level || 'INFO').toUpperCase()}
Subtype / Engine: ${log.subtype || log.type || 'traffic'}
Action: ${(log.action || 'N/A').toUpperCase()}

Network Path:
Source: ${log.srcip || log.sourceIp || 'N/A'} : ${log.srcport || 'N/A'} (${log.srccountry || 'N/A'})
Destination: ${log.dstip || log.destIp || 'N/A'} : ${log.dstport || 'N/A'} (${log.dstcountry || log.destCountry || 'N/A'})
Protocol / Service: ${log.proto || 'N/A'} / ${log.service || log.app || 'N/A'}
Policy: ${log.policyname || log.policyid || 'N/A'}

Automated Forensic Narrative:
${narrative.what}
${narrative.why}
Risk Level: ${narrative.isThreat}
Recommended Action: ${narrative.action}
            `.trim();

            const res = await axios.post(`${backendURL}/api/zoho/tickets`, {
                userName: "QueryTel SOC Automations",
                userEmail: "soc@querytel.com",
                issueDescription: description
            }, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            if (res.status === 200 || res.data?.success || res.data?.ok) {
                setZohoStatus(prev => ({ ...prev, [logId]: 'created' }));
                triggerToast(`🎫 Zoho Ticket created for log ${String(log._id).slice(-6)}!`, "success");
            } else {
                throw new Error("Zoho creation failed");
            }
        } catch (err) {
            console.error("Failed to create Zoho ticket:", err);
            setZohoStatus(prev => ({ ...prev, [logId]: 'error' }));
            triggerToast("Failed to create Zoho ticket", "error");
        }
    };

    // Safe clipboard copy helper (works in all HTTP / HTTPS / iframe browser contexts)
    const copyToClipboard = async (text) => {
        try {
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                return true;
            }
        } catch (err) {
            console.warn("navigator.clipboard.writeText failed, using fallback:", err);
        }

        try {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.style.position = "fixed";
            textarea.style.left = "-9999px";
            textarea.style.top = "-9999px";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            const successful = document.execCommand("copy");
            document.body.removeChild(textarea);
            return !!successful;
        } catch (e) {
            console.error("Fallback execCommand copy failed:", e);
            return false;
        }
    };

    // Copy raw JSON
    const copyRawPayload = async (obj) => {
        const text = typeof obj === 'string' ? obj : JSON.stringify(obj, null, 2);
        const ok = await copyToClipboard(text);
        if (ok) {
            setCopiedRaw(true);
            setTimeout(() => setCopiedRaw(false), 2500);
            triggerToast("📋 FortiOS Syslog JSON copied to clipboard!", "success");
        } else {
            triggerToast("⚠️ Clipboard copy not allowed in current browser context", "error");
        }
    };

    // Format Bytes
    const formatBytes = (bytes) => {
        const b = parseInt(bytes || 0);
        if (b === 0) return '0 B';
        if (b > 1024 * 1024 * 1024) return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
        if (b > 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(2)} MB`;
        if (b > 1024) return `${(b / 1024).toFixed(1)} KB`;
        return `${b} B`;
    };

    // Action Breakdown Data for chart
    const actionChartData = useMemo(() => {
        if (!summary?.actions) return [];
        return summary.actions.slice(0, 5).map(a => ({
            name: a.action.toUpperCase(),
            value: a.count,
            color: ACTION_COLORS[a.action] || "#64748B"
        }));
    }, [summary]);

    // Top Gateways Data for chart
    const topGatewaysData = useMemo(() => {
        if (!summary?.activeDevices) return [];
        return summary.activeDevices.slice(0, 5);
    }, [summary]);

    // Top Destinations Data
    const topDestinationsData = useMemo(() => {
        if (!summary?.topDestinations) return [];
        const total = summary.topDestinations.reduce((acc, curr) => acc + curr.value, 0) || 1;
        return summary.topDestinations.slice(0, 5).map(d => ({
            code: d.name,
            name: COUNTRY_NAMES[d.name] || d.name,
            count: d.value,
            pct: Math.round((d.value / total) * 100)
        }));
    }, [summary]);

    return (
        <div className="min-h-screen bg-[#090D16] text-slate-300 font-sans p-4 sm:p-6 overflow-x-hidden selection:bg-blue-500/30">
            {/* TOAST NOTIFICATION */}
            <AnimatePresence>
                {notification && (
                    <motion.div
                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                        onClick={() => {
                            if (notification.link) navigate(notification.link);
                        }}
                        className={`fixed top-6 right-6 z-[100] px-5 py-3 rounded-2xl border shadow-2xl flex items-center gap-3 backdrop-blur-xl text-xs font-bold ${notification.link ? 'cursor-pointer hover:scale-[1.02] transition-transform' : ''} ${notification.type === 'error'
                            ? 'bg-rose-950/80 border-rose-500/40 text-rose-200'
                            : 'bg-emerald-950/80 border-emerald-500/40 text-emerald-200'
                            }`}
                    >
                        {notification.type === 'error' ? (
                            <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
                        ) : (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                        )}
                        <span>{notification.msg}</span>
                        {notification.linkText && (
                            <span className="ml-2 underline text-emerald-300 font-black flex items-center gap-1">
                                {notification.linkText}
                            </span>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* TOP HEADER & TELEMETRY PULSE */}
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-6 pb-6 border-b border-white/[0.06]">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-600/10 rounded-2xl flex items-center justify-center border border-blue-500/30 shadow-[0_0_35px_rgba(37,99,235,0.2)]">
                        <Radar className="w-8 h-8 text-blue-400 animate-pulse" />
                    </div>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl sm:text-3xl font-black text-slate-100 tracking-tight uppercase italic flex items-center gap-2">
                                EVENT <span className="text-blue-500">EXPLORER</span>
                            </h1>
                            <span className="px-2.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-[10px] font-mono font-bold text-blue-400 tracking-wider">
                                FORTIOS 7.4 TELEMETRY CORE
                            </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                            <span className="flex items-center gap-1.5 font-bold text-emerald-400">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
                                Live Ingestion Active
                            </span>
                            <span className="text-slate-600">•</span>
                            <span className="font-mono text-slate-400">
                                Synced {lastSyncTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                            <span className="text-slate-600">•</span>
                            <span className="font-mono text-slate-400">
                                Total Monitored: <strong className="text-slate-100 font-bold">{(totalLogs || summary?.totalLogs || 0).toLocaleString()}</strong>
                            </span>
                        </div>
                    </div>
                </div>

                {/* Stream Controls & Refresh Bar */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Stream Play/Pause & Speed */}
                    <div className="flex items-center bg-[#111827] border border-white/[0.08] rounded-xl p-1 shadow-inner">
                        <button
                            onClick={() => setIsStreaming(!isStreaming)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${isStreaming
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                                }`}
                            title={isStreaming ? "Pause Live Polling" : "Resume Live Polling"}
                        >
                            {isStreaming ? (
                                <>
                                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                                    LIVE
                                </>
                            ) : (
                                <>
                                    <Pause className="w-3 h-3 text-amber-400" />
                                    PAUSED
                                </>
                            )}
                        </button>

                        <select
                            value={refreshInterval}
                            onChange={(e) => setRefreshInterval(Number(e.target.value))}
                            className="bg-transparent text-[10px] font-mono font-bold text-slate-400 px-2 py-1 outline-none cursor-pointer hover:text-slate-200"
                        >
                            <option value={3000} className="bg-[#111827]">3s rate</option>
                            <option value={5000} className="bg-[#111827]">5s rate</option>
                            <option value={10000} className="bg-[#111827]">10s rate</option>
                            <option value={30000} className="bg-[#111827]">30s rate</option>
                        </select>
                    </div>

                    {/* Quick Link to Firewall Approvals */}
                    <button
                        onClick={() => navigate('/firewall-approvals')}
                        className="px-3 py-2 bg-[#111827] border border-white/[0.08] rounded-xl text-xs font-mono font-bold text-slate-300 hover:text-emerald-400 hover:border-emerald-500/30 transition-all flex items-center gap-1.5"
                        title="Open Firewall Change & Approval Queue"
                    >
                        <Shield className="w-3.5 h-3.5 text-emerald-400" />
                        <span>Approvals Queue</span>
                        <ArrowUpRight className="w-3.5 h-3.5 text-slate-500" />
                    </button>

                    {/* Manual Refresh Button */}
                    <button
                        onClick={() => { fetchLogs(); fetchSummary(); }}
                        disabled={isRefreshing}
                        className="p-2.5 bg-[#111827] border border-white/[0.08] rounded-xl text-slate-300 hover:text-blue-400 hover:border-blue-500/30 transition-all shadow-md active:scale-95 disabled:opacity-50"
                        title="Force Immediate Telemetry Sync"
                    >
                        <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin text-blue-400' : ''}`} />
                    </button>
                </div>
            </div>

            {/* REAL ANALYTICAL WIDGETS GRID (4 PANELS - 100% REAL DATA) */}
            <div className="grid grid-cols-12 gap-4 mb-6">
                {/* 1. REAL 24H TELEMETRY TIMELINE */}
                <div className="col-span-12 lg:col-span-4 bg-[#111827] border border-white/[0.08] rounded-2xl p-4 shadow-xl relative overflow-hidden group hover:border-blue-500/30 transition-all">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-blue-400" />
                            <h3 className="text-[11px] font-black text-slate-200 uppercase tracking-wider">24h Event Volume</h3>
                        </div>
                        <span className="text-[9px] font-mono text-slate-500 uppercase">Hourly Distribution</span>
                    </div>

                    <div className="h-40">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={summary?.hourlyTrend || []} margin={{ top: 5, right: 0, left: -25, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4} />
                                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.0} />
                                    </linearGradient>
                                    <linearGradient id="highGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.4} />
                                        <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="2 2" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="hour" stroke="#475569" fontSize={8} tickLine={false} axisLine={false} />
                                <YAxis stroke="#475569" fontSize={8} tickLine={false} axisLine={false} />
                                <Tooltip
                                    contentStyle={{ background: '#0B1120', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', fontSize: '11px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}
                                    labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                                />
                                <Area type="monotone" dataKey="low" name="Routine Logs" stroke="#3B82F6" fillOpacity={1} fill="url(#volGrad)" strokeWidth={1.5} />
                                <Area type="monotone" dataKey="medium" name="Warnings" stroke="#F59E0B" fillOpacity={1} fill="url(#highGrad)" strokeWidth={1.5} />
                                <Area type="monotone" dataKey="high" name="Errors/Critical" stroke="#EF4444" fillOpacity={0} strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* 2. REAL TOP ACTIVE FIREWALL FLEET */}
                <div className="col-span-12 sm:col-span-6 lg:col-span-3 bg-[#111827] border border-white/[0.08] rounded-2xl p-4 shadow-xl relative group hover:border-emerald-500/30 transition-all">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Server className="w-4 h-4 text-emerald-400" />
                            <h3 className="text-[11px] font-black text-slate-200 uppercase tracking-wider">Top Gateways</h3>
                        </div>
                        <span className="text-[9px] font-mono text-emerald-400 font-bold">{summary?.totalGateways || 10} Nodes</span>
                    </div>

                    <div className="space-y-2.5 h-40 overflow-y-auto custom-scrollbar pr-1 flex flex-col justify-center">
                        {topGatewaysData.map((gw, idx) => {
                            const maxVal = topGatewaysData[0]?.value || 1;
                            const pct = Math.round((gw.value / maxVal) * 100);
                            return (
                                <div
                                    key={gw.name || idx}
                                    onClick={() => setSelectedGateway(gw.name)}
                                    className={`group/item p-1.5 rounded-lg border transition-all cursor-pointer ${selectedGateway === gw.name
                                        ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                                        : 'bg-black/20 border-white/[0.04] hover:border-emerald-500/20 text-slate-300'
                                        }`}
                                >
                                    <div className="flex justify-between items-center text-[10px] font-mono mb-1">
                                        <span className="font-bold truncate max-w-[140px] group-hover/item:text-emerald-400 transition-colors">
                                            {gw.name}
                                        </span>
                                        <span className="text-slate-400 font-bold">{gw.value.toLocaleString()}</span>
                                    </div>
                                    <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${pct}%` }}
                                            transition={{ duration: 0.8 }}
                                            className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full"
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* 3. REAL TOP GLOBAL DESTINATION NATIONS */}
                <div className="col-span-12 sm:col-span-6 lg:col-span-3 bg-[#111827] border border-white/[0.08] rounded-2xl p-4 shadow-xl relative group hover:border-indigo-500/30 transition-all">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-indigo-400" />
                            <h3 className="text-[11px] font-black text-slate-200 uppercase tracking-wider">Top Destinations</h3>
                        </div>
                        <span className="text-[9px] font-mono text-slate-500 uppercase">Live GeoIP</span>
                    </div>

                    <div className="space-y-2.5 h-40 overflow-y-auto custom-scrollbar pr-1 flex flex-col justify-center">
                        {topDestinationsData.map((dest, idx) => (
                            <div key={dest.code || idx} className="flex items-center gap-3">
                                <span className="text-[10px] font-mono font-black text-indigo-400 w-7 px-1 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/20 text-center">
                                    {dest.code}
                                </span>
                                <div className="flex-1">
                                    <div className="flex justify-between text-[10px] font-mono mb-1">
                                        <span className="text-slate-300 font-bold truncate max-w-[100px]">{dest.name}</span>
                                        <span className="text-slate-400">{dest.count.toLocaleString()}</span>
                                    </div>
                                    <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${dest.pct}%` }}
                                            transition={{ duration: 0.8 }}
                                            className="h-full bg-indigo-500 rounded-full"
                                        />
                                    </div>
                                </div>
                                <span className="text-[9px] font-mono text-slate-400 w-8 text-right font-bold">{dest.pct}%</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 4. REAL POLICY ENFORCEMENT & ACTION RATIO */}
                <div className="col-span-12 lg:col-span-2 bg-[#111827] border border-white/[0.08] rounded-2xl p-4 shadow-xl relative group hover:border-rose-500/30 transition-all">
                    <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-rose-400" />
                            <h3 className="text-[11px] font-black text-slate-200 uppercase tracking-wider">Action Ratio</h3>
                        </div>
                    </div>

                    <div className="h-32 flex items-center justify-center relative">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={actionChartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={32}
                                    outerRadius={48}
                                    paddingAngle={4}
                                    dataKey="value"
                                >
                                    {actionChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ background: '#0B1120', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', fontSize: '10px' }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="flex items-center justify-around text-[9px] font-mono font-bold text-slate-400 border-t border-white/[0.04] pt-2">
                        <span className="flex items-center gap-1 text-emerald-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Accept
                        </span>
                        <span className="flex items-center gap-1 text-rose-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span> Deny
                        </span>
                        <span className="flex items-center gap-1 text-slate-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-500"></span> Close
                        </span>
                    </div>
                </div>
            </div>

            {/* ENTERPRISE FILTER SUITE & SEARCH BAR */}
            <div className="bg-[#111827] border border-white/[0.08] rounded-2xl p-4 mb-6 shadow-2xl">
                <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
                    {/* Free Text Search */}
                    <div className="relative flex-1 group">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                        <input
                            type="text"
                            placeholder="Live Search IP (192.168.x.x, 8.8.8.8), Gateway, Policy name, App, Signature..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-[#0B1120] border border-white/[0.08] rounded-xl pl-10 pr-10 py-2.5 text-xs font-mono text-slate-100 placeholder-slate-500 outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/20 transition-all shadow-inner"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 p-1"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* Engine Filter Bar */}
                    <div className="flex items-center bg-[#0B1120] border border-white/[0.08] rounded-xl p-0.5 text-xs">
                        {[
                            { id: "all", label: "All Engines" },
                            { id: "fortigate", label: "🔥 FortiGate" },
                            { id: "defender", label: "🛡️ Defender XDR" },
                            { id: "threatdown", label: "⚡ ThreatDown EDR" }
                        ].map((eng) => (
                            <button
                                key={eng.id}
                                onClick={() => { setSelectedEngine(eng.id); setPage(1); }}
                                className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition-all cursor-pointer ${
                                    selectedEngine === eng.id
                                        ? "bg-blue-600 text-white shadow-md"
                                        : "text-slate-400 hover:text-slate-200"
                                }`}
                            >
                                {eng.label}
                            </button>
                        ))}
                    </div>

                    {/* Filter Dropdowns Grid */}
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Gateway Filter */}
                        <div className="relative">
                            <select
                                value={selectedGateway}
                                onChange={(e) => { setSelectedGateway(e.target.value); setPage(1); }}
                                className="bg-[#0B1120] border border-white/[0.08] rounded-xl px-3 py-2 text-xs font-mono font-bold text-slate-200 outline-none focus:border-blue-500/50 cursor-pointer appearance-none pr-8 hover:bg-[#131C2E] transition-all"
                            >
                                <option value="all">🌐 All Gateways ({summary?.totalGateways || 10})</option>
                                {(summary?.activeDevices || []).map(gw => (
                                    <option key={gw.name} value={gw.name}>
                                        {gw.name} ({gw.value?.toLocaleString()})
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>

                        {/* Subtype / Engine Filter */}
                        <div className="relative">
                            <select
                                value={selectedSubtype}
                                onChange={(e) => { setSelectedSubtype(e.target.value); setPage(1); }}
                                className="bg-[#0B1120] border border-white/[0.08] rounded-xl px-3 py-2 text-xs font-mono font-bold text-slate-200 outline-none focus:border-blue-500/50 cursor-pointer appearance-none pr-8 hover:bg-[#131C2E] transition-all"
                            >
                                <option value="all">⚡ All Subtypes</option>
                                <option value="forward">forward (Traffic)</option>
                                <option value="local">local (System)</option>
                                <option value="app-ctrl">app-ctrl (App Control)</option>
                                <option value="vpn">vpn (IPsec / SSL VPN)</option>
                                <option value="dns">dns (DNS Filter)</option>
                                <option value="webfilter">webfilter (Web Filter)</option>
                                <option value="ssl">ssl (SSL Inspection)</option>
                                <option value="file-filter">file-filter (DLP / Files)</option>
                                <option value="ips">ips (Intrusion Prevention)</option>
                                <option value="virus">virus (Antivirus)</option>
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>

                        {/* Action Filter */}
                        <div className="relative">
                            <select
                                value={selectedAction}
                                onChange={(e) => { setSelectedAction(e.target.value); setPage(1); }}
                                className="bg-[#0B1120] border border-white/[0.08] rounded-xl px-3 py-2 text-xs font-mono font-bold text-slate-200 outline-none focus:border-blue-500/50 cursor-pointer appearance-none pr-8 hover:bg-[#131C2E] transition-all"
                            >
                                <option value="all">🛡️ All Actions</option>
                                <option value="accept">ACCEPT</option>
                                <option value="deny">DENY / BLOCK</option>
                                <option value="close">CLOSE</option>
                                <option value="pass">PASS</option>
                                <option value="client-rst">CLIENT-RST</option>
                                <option value="server-rst">SERVER-RST</option>
                                <option value="timeout">TIMEOUT</option>
                                <option value="ssl-alert">SSL-ALERT</option>
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>

                        {/* Severity Filter */}
                        <div className="relative">
                            <select
                                value={selectedSeverity}
                                onChange={(e) => { setSelectedSeverity(e.target.value); setPage(1); }}
                                className="bg-[#0B1120] border border-white/[0.08] rounded-xl px-3 py-2 text-xs font-mono font-bold text-slate-200 outline-none focus:border-blue-500/50 cursor-pointer appearance-none pr-8 hover:bg-[#131C2E] transition-all"
                            >
                                <option value="all">🔴 All Severities</option>
                                <option value="critical">Critical / Alert</option>
                                <option value="error">Error / Fault</option>
                                <option value="warning">Warning / Anomaly</option>
                                <option value="notice">Notice</option>
                                <option value="info">Informational</option>
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>

                        {/* Time Range Selector */}
                        <div className="relative">
                            <select
                                value={timeRange}
                                onChange={(e) => { setTimeRange(e.target.value); setPage(1); }}
                                className="bg-[#0B1120] border border-white/[0.08] rounded-xl px-3 py-2 text-xs font-mono font-bold text-slate-200 outline-none focus:border-blue-500/50 cursor-pointer appearance-none pr-8 hover:bg-[#131C2E] transition-all"
                            >
                                <option value="all">⏱️ All History</option>
                                <option value="15m">Last 15 Minutes</option>
                                <option value="1h">Last 1 Hour</option>
                                <option value="6h">Last 6 Hours</option>
                                <option value="24h">Last 24 Hours</option>
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* Active Filter Chips */}
                {activeFilterChips.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/[0.04]">
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider flex items-center gap-1 mr-1">
                            <Filter className="w-3 h-3 text-blue-400" /> Active Filters:
                        </span>
                        {activeFilterChips.map((chip, idx) => (
                            <div
                                key={idx}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-300 text-[11px] font-mono font-bold"
                            >
                                <span>{chip.label}</span>
                                <button
                                    onClick={chip.reset}
                                    className="p-0.5 hover:bg-blue-500/20 rounded text-blue-400 hover:text-white transition-colors"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                        <button
                            onClick={resetAllFilters}
                            className="text-[10px] font-mono text-rose-400 hover:text-rose-300 ml-2 underline underline-offset-4 cursor-pointer"
                        >
                            Clear All Filters
                        </button>
                    </div>
                )}
            </div>

            {/* LIVE SOC EVENT STREAM TABLE */}
            <div className="bg-[#111827] border border-white/[0.08] rounded-2xl shadow-2xl overflow-hidden flex flex-col min-h-[550px]">
                {/* Table Header Bar */}
                <div className="px-6 py-3.5 bg-[#0D1424] border-b border-white/[0.08] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></div>
                        <h2 className="text-xs font-black text-slate-200 uppercase tracking-widest flex items-center gap-2">
                            Unified Multi-Vendor Telemetry Stream
                        </h2>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-400 font-bold">
                            Showing {logs.length} of {totalLogs.toLocaleString()} Events
                        </span>
                    </div>

                    <div className="flex items-center gap-3">
                        <span className="text-[10px] font-mono text-slate-400 hidden sm:inline">
                            Page {page} of {totalPages}
                        </span>
                    </div>
                </div>

                {/* Table Rows */}
                <div className="flex-1 overflow-x-auto custom-scrollbar">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center p-28 text-slate-500 gap-3">
                            <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                            <span className="text-xs font-mono font-bold uppercase tracking-widest text-slate-400">
                                Querying Multi-Vendor Telemetry Stream...
                            </span>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center p-28 text-slate-500 gap-3">
                            <ShieldAlert className="w-10 h-10 text-slate-600" />
                            <span className="text-xs font-mono font-bold uppercase tracking-wider text-slate-400">
                                No Telemetry matching active filter parameters.
                            </span>
                            <button
                                onClick={resetAllFilters}
                                className="mt-2 px-4 py-1.5 rounded-xl bg-blue-600/20 border border-blue-500/40 text-blue-400 text-xs font-bold hover:bg-blue-600/30 transition-all"
                            >
                                Reset Filters
                            </button>
                        </div>
                    ) : (
                        <div className="divide-y divide-white/[0.04] min-w-[900px]">
                            {logs.map((log) => {
                                const narrative = analyzeLog(log);
                                const ts = log.ts || log.timestamp || log.devtime || log.date;
                                const timeFormatted = ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'N/A';
                                const dateFormatted = ts ? new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '';
                                
                                const gateway = log.devname || log.deviceName || 'Perimeter';
                                const action = (log.action || 'N/A').toLowerCase();
                                const subtype = (log.subtype || log.type || 'traffic').toLowerCase();
                                const severity = (log.severity || log.level || 'info').toLowerCase();
                                const isBlockAction = action.includes('block') || action.includes('deny') || action.includes('drop') || action.includes('blocked');

                                const srcIp = log.srcip || log.sourceIp || log.remip || 'Internal';
                                const dstIp = log.dstip || log.destIp || 'External';
                                const srcPort = log.srcport || log.sourcePort;
                                const dstPort = log.dstport || log.destPort;
                                const service = log.service || log.app || log.proto || '';

                                return (
                                    <div
                                        key={log._id}
                                        onClick={() => setSelectedLog(log)}
                                        className="px-5 py-3 hover:bg-[#131D33] transition-all cursor-pointer flex items-center gap-4 group"
                                    >
                                        {/* Severity Accent Left Bar */}
                                        <div
                                            className="w-1.5 self-stretch rounded-full shrink-0"
                                            style={{ backgroundColor: SEVERITY_COLORS[severity] || '#60A5FA' }}
                                        />

                                        {/* Timestamp */}
                                        <div className="w-24 shrink-0 flex flex-col font-mono text-[11px]">
                                            <span className="font-bold text-slate-200">{timeFormatted}</span>
                                            <span className="text-[9px] text-slate-500">{dateFormatted}</span>
                                        </div>

                                        {/* Gateway & Vendor Engine Badge */}
                                        <div className="w-40 shrink-0 flex items-center gap-1.5">
                                            {log.sourceType?.includes("Defender") ? (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#00A4EF]/20 border border-[#00A4EF]/40 text-[9px] font-mono font-bold text-[#38BDF8] shrink-0">
                                                    XDR
                                                </span>
                                            ) : log.sourceType?.includes("ThreatDown") ? (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#00D26A]/20 border border-[#00D26A]/40 text-[9px] font-mono font-bold text-[#00D26A] shrink-0">
                                                    EDR
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-500/20 border border-indigo-500/30 text-[9px] font-mono font-bold text-indigo-400 shrink-0">
                                                    FW
                                                </span>
                                            )}
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-800/80 border border-white/[0.08] text-[10px] font-mono font-bold text-slate-300 truncate max-w-full">
                                                <Server className="w-3 h-3 text-emerald-400 shrink-0" />
                                                <span className="truncate">{gateway}</span>
                                            </span>
                                        </div>

                                        {/* Action Badge */}
                                        <div className="w-24 shrink-0">
                                            <span
                                                className="inline-block px-2.5 py-0.5 rounded text-[10px] font-mono font-black uppercase tracking-wider text-center w-full"
                                                style={{
                                                    backgroundColor: `${ACTION_COLORS[action] || '#64748B'}20`,
                                                    color: ACTION_COLORS[action] || '#94A3B8',
                                                    border: `1px solid ${ACTION_COLORS[action] || '#64748B'}40`
                                                }}
                                            >
                                                {action.toUpperCase()}
                                            </span>
                                        </div>

                                        {/* Subtype & Protocol */}
                                        <div className="w-24 shrink-0 hidden md:block">
                                            <div className="text-[10px] font-mono font-bold text-blue-400 truncate">
                                                {subtype.toUpperCase()}
                                            </div>
                                            <div className="text-[9px] font-mono text-slate-500 truncate">
                                                {service || (log.proto ? `proto:${log.proto}` : 'TCP/UDP')}
                                            </div>
                                        </div>

                                        {/* Network Flow (Source -> Destination) */}
                                        <div className="w-64 shrink-0 font-mono text-[11px] hidden lg:block">
                                            <div className="flex items-center gap-1.5 text-slate-300 font-bold truncate">
                                                <AbuseIPDBPopover ip={srcIp} className="text-blue-400 hover:underline">
                                                    {srcIp}
                                                </AbuseIPDBPopover>
                                                {srcPort && <span className="text-slate-500 text-[9px]">:{srcPort}</span>}
                                                <span className="text-slate-600">→</span>
                                                <AbuseIPDBPopover ip={dstIp} className="text-indigo-400 hover:underline">
                                                    {dstIp}
                                                </AbuseIPDBPopover>
                                                {dstPort && <span className="text-slate-500 text-[9px]">:{dstPort}</span>}
                                            </div>
                                            <div className="text-[9px] text-slate-500 flex items-center gap-2">
                                                {log.srccountry && log.srccountry !== 'Reserved' && (
                                                    <span>From: {log.srccountry}</span>
                                                )}
                                                {log.dstcountry && (
                                                    <span>To: {log.dstcountry}</span>
                                                )}
                                                {log.policyname && (
                                                    <span className="text-slate-400 truncate">Rule: "{log.policyname}"</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Narrative / Explanation */}
                                        <div className="flex-1 truncate">
                                            <p className="text-xs text-slate-300 font-medium truncate group-hover:text-white transition-colors">
                                                {narrative.what}
                                            </p>
                                            <p className="text-[10px] text-slate-500 truncate italic">
                                                {narrative.why}
                                            </p>
                                        </div>

                                        {/* Quick Actions (Stage Firewall Block + Zoho Ticket) */}
                                        <div className="flex items-center gap-2 shrink-0">
                                            {/* Stage Block Button (only if not already internal) */}
                                            {srcIp && srcIp !== 'Internal' && !srcIp.startsWith('127.') && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (stagingStatus[log._id] === 'staged') {
                                                            navigate('/firewall-approvals');
                                                        } else {
                                                            handleStageFirewallBlock(e, log);
                                                        }
                                                    }}
                                                    disabled={stagingStatus[log._id] === 'staging'}
                                                    className={`px-2.5 py-1 rounded-lg border text-[9px] font-mono font-bold uppercase tracking-wider flex items-center gap-1.5 transition-all ${stagingStatus[log._id] === 'staged'
                                                        ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/30 ring-1 ring-emerald-400/40 cursor-pointer'
                                                        : 'bg-rose-600/10 border-rose-500/20 text-rose-300 hover:bg-rose-600/20 hover:border-rose-500/40 cursor-pointer'
                                                        }`}
                                                    title={stagingStatus[log._id] === 'staged' ? "Click to open Firewall Approvals queue" : `Stage Firewall Rule to block ${srcIp} on ${gateway}`}
                                                >
                                                    {stagingStatus[log._id] === 'staging' ? (
                                                        <RefreshCw className="w-3 h-3 animate-spin" />
                                                    ) : stagingStatus[log._id] === 'staged' ? (
                                                        <>
                                                            <ExternalLink className="w-3 h-3 text-emerald-400" />
                                                            View in Approvals →
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ShieldAlert className="w-3 h-3 text-rose-400" />
                                                            Stage Block
                                                        </>
                                                    )}
                                                </button>
                                            )}

                                            {/* Zoho Ticket Button */}
                                            <button
                                                onClick={(e) => handleCreateZohoTicket(e, log)}
                                                disabled={zohoStatus[log._id] === 'loading' || zohoStatus[log._id] === 'created'}
                                                className={`px-2.5 py-1 rounded-lg border text-[9px] font-mono font-bold uppercase tracking-wider flex items-center gap-1 transition-all ${zohoStatus[log._id] === 'created'
                                                    ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300'
                                                    : 'bg-blue-600/10 border-blue-500/20 text-blue-300 hover:bg-blue-600/20 hover:border-blue-500/40'
                                                    }`}
                                                title="Create Zoho Desk Incident Ticket"
                                            >
                                                {zohoStatus[log._id] === 'loading' ? (
                                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                                ) : zohoStatus[log._id] === 'created' ? (
                                                    <>
                                                        <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                                                        Ticket
                                                    </>
                                                ) : (
                                                    <>
                                                        <Ticket className="w-3 h-3 text-blue-400" />
                                                        Zoho
                                                    </>
                                                )}
                                            </button>

                                            <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* PAGINATION FOOTER */}
                <div className="px-6 py-4 bg-[#0D1424] border-t border-white/[0.08] flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex items-center gap-4 text-xs font-mono text-slate-400">
                        <span>
                            Showing <strong className="text-slate-100">{logs.length > 0 ? (page - 1) * limit + 1 : 0}</strong> to <strong className="text-slate-100">{Math.min(page * limit, totalLogs)}</strong> of <strong className="text-slate-100">{totalLogs.toLocaleString()}</strong> events
                        </span>

                        <div className="flex items-center gap-2">
                            <span className="text-slate-500 text-[10px] uppercase">Per Page:</span>
                            <select
                                value={limit}
                                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                                className="bg-[#111827] border border-white/[0.08] rounded-lg px-2 py-1 text-xs font-mono text-slate-300 outline-none cursor-pointer"
                            >
                                <option value={15}>15</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>

                    {/* Page Navigator */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(1)}
                            disabled={page === 1}
                            className="px-2.5 py-1.5 rounded-lg bg-[#111827] border border-white/[0.08] text-xs font-mono font-bold text-slate-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-all"
                        >
                            First
                        </button>
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="p-1.5 rounded-lg bg-[#111827] border border-white/[0.08] text-slate-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-all"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <div className="px-3 py-1 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 font-mono text-xs font-bold">
                            Page {page} / {totalPages || 1}
                        </div>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            className="p-1.5 rounded-lg bg-[#111827] border border-white/[0.08] text-slate-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-all"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setPage(totalPages)}
                            disabled={page >= totalPages}
                            className="px-2.5 py-1.5 rounded-lg bg-[#111827] border border-white/[0.08] text-xs font-mono font-bold text-slate-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-all"
                        >
                            Last
                        </button>
                    </div>
                </div>
            </div>

            {/* SLIDE-OVER FORENSIC INSPECTION DRAWER */}
            <AnimatePresence>
                {selectedLog && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedLog(null)}
                            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[90]"
                        />

                        {/* Slide-in Drawer */}
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-[#0C121E] border-l border-white/[0.1] shadow-2xl z-[95] flex flex-col overflow-hidden"
                        >
                            {/* Drawer Header */}
                            <div className="p-6 border-b border-white/[0.08] bg-[#0E1626] flex items-center justify-between">
                                <div>
                                    <div className="flex items-center gap-2.5">
                                        <span
                                            className="px-2 py-0.5 rounded text-[10px] font-mono font-black uppercase tracking-wider"
                                            style={{
                                                backgroundColor: `${SEVERITY_COLORS[selectedLog.severity || selectedLog.level || 'info']}20`,
                                                color: SEVERITY_COLORS[selectedLog.severity || selectedLog.level || 'info'],
                                                border: `1px solid ${SEVERITY_COLORS[selectedLog.severity || selectedLog.level || 'info']}40`
                                            }}
                                        >
                                            {(selectedLog.severity || selectedLog.level || 'INFO').toUpperCase()}
                                        </span>
                                        <span className="font-mono text-xs text-slate-400 font-bold">
                                            {selectedLog.devname || selectedLog.deviceName || 'Perimeter Gateway'}
                                        </span>
                                    </div>
                                    <h3 className="text-base font-black text-slate-100 tracking-tight mt-1 font-mono">
                                        Log Record: {String(selectedLog._id)}
                                    </h3>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => copyRawPayload(selectedLog)}
                                        className={`p-2 px-3 rounded-xl border text-xs font-mono font-bold flex items-center gap-1.5 transition-all ${copiedRaw
                                            ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300'
                                            : 'bg-slate-800 border-white/[0.08] hover:bg-slate-700 text-slate-300 hover:text-white'
                                            }`}
                                        title="Copy Full FortiOS Syslog JSON"
                                    >
                                        {copiedRaw ? (
                                            <>
                                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                                <span className="text-[10px] text-emerald-400">Copied!</span>
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="w-4 h-4 text-slate-400" />
                                                <span className="text-[10px]">Copy JSON</span>
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setSelectedLog(null)}
                                        className="p-2 rounded-xl bg-slate-800 hover:bg-rose-500/20 hover:text-rose-400 text-slate-400 transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Drawer Navigation Tabs */}
                            <div className="flex items-center gap-2 px-6 pt-4 border-b border-white/[0.06] bg-[#0A0F1A]">
                                <button
                                    onClick={() => setDrawerTab("overview")}
                                    className={`px-4 py-2 text-xs font-mono font-bold uppercase tracking-wider border-b-2 transition-all ${drawerTab === 'overview'
                                        ? 'border-blue-500 text-blue-400 bg-blue-500/10 rounded-t-lg'
                                        : 'border-transparent text-slate-400 hover:text-slate-200'
                                        }`}
                                >
                                    Forensic Overview
                                </button>
                                <button
                                    onClick={() => setDrawerTab("attributes")}
                                    className={`px-4 py-2 text-xs font-mono font-bold uppercase tracking-wider border-b-2 transition-all ${drawerTab === 'attributes'
                                        ? 'border-blue-500 text-blue-400 bg-blue-500/10 rounded-t-lg'
                                        : 'border-transparent text-slate-400 hover:text-slate-200'
                                        }`}
                                >
                                    Parsed FortiOS Attributes ({Object.keys(selectedLog).length})
                                </button>
                                <button
                                    onClick={() => setDrawerTab("raw")}
                                    className={`px-4 py-2 text-xs font-mono font-bold uppercase tracking-wider border-b-2 transition-all ${drawerTab === 'raw'
                                        ? 'border-blue-500 text-blue-400 bg-blue-500/10 rounded-t-lg'
                                        : 'border-transparent text-slate-400 hover:text-slate-200'
                                        }`}
                                >
                                    Raw Syslog JSON
                                </button>
                            </div>

                            {/* Drawer Body */}
                            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
                                {drawerTab === 'overview' && (() => {
                                    const narrative = analyzeLog(selectedLog);
                                    const srcIp = selectedLog.srcip || selectedLog.sourceIp;
                                    const dstIp = selectedLog.dstip || selectedLog.destIp;

                                    return (
                                        <div className="space-y-6">
                                            {/* Action / Threat Banner */}
                                            <div className="p-4 rounded-2xl bg-[#11192A] border border-white/[0.08] space-y-3">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                        THREAT ASSESSMENT
                                                    </span>
                                                    <span className={`text-xs font-black uppercase font-mono ${narrative.riskColor}`}>
                                                        {narrative.isThreat} (Score: {narrative.riskScore}/100)
                                                    </span>
                                                </div>
                                                <p className="text-sm font-bold text-slate-100 leading-relaxed">
                                                    {narrative.what}
                                                </p>
                                                <p className="text-xs text-slate-400 leading-relaxed italic">
                                                    {narrative.why}
                                                </p>
                                            </div>

                                            {/* Network Flow Cards */}
                                            <div className="grid grid-cols-2 gap-4">
                                                {/* Source */}
                                                <div className="p-4 rounded-xl bg-[#090E18] border border-white/[0.06] space-y-2">
                                                    <span className="text-[10px] font-mono font-bold text-blue-400 uppercase">
                                                        SOURCE INGRESS
                                                    </span>
                                                    <div className="text-sm font-mono font-black text-slate-100">
                                                        <AbuseIPDBPopover ip={srcIp} className="hover:underline text-blue-400">
                                                            {srcIp || 'Internal'}
                                                        </AbuseIPDBPopover>
                                                    </div>
                                                    <div className="text-[11px] font-mono text-slate-400 space-y-1">
                                                        <div>Port: <strong className="text-slate-200">{selectedLog.srcport || selectedLog.sourcePort || 'N/A'}</strong></div>
                                                        <div>Interface: <strong className="text-slate-200">{selectedLog.srcintf || selectedLog.srcintfrole || 'N/A'}</strong></div>
                                                        <div>Geo: <strong className="text-slate-200">{selectedLog.srccountry || selectedLog.srcCountry || 'N/A'}</strong></div>
                                                    </div>
                                                </div>

                                                {/* Destination */}
                                                <div className="p-4 rounded-xl bg-[#090E18] border border-white/[0.06] space-y-2">
                                                    <span className="text-[10px] font-mono font-bold text-indigo-400 uppercase">
                                                        DESTINATION EGRESS
                                                    </span>
                                                    <div className="text-sm font-mono font-black text-slate-100">
                                                        <AbuseIPDBPopover ip={dstIp} className="hover:underline text-indigo-400">
                                                            {dstIp || 'External'}
                                                        </AbuseIPDBPopover>
                                                    </div>
                                                    <div className="text-[11px] font-mono text-slate-400 space-y-1">
                                                        <div>Port: <strong className="text-slate-200">{selectedLog.dstport || selectedLog.destPort || 'N/A'}</strong></div>
                                                        <div>Interface: <strong className="text-slate-200">{selectedLog.dstintf || selectedLog.dstintfrole || 'N/A'}</strong></div>
                                                        <div>Geo: <strong className="text-slate-200">{selectedLog.dstcountry || selectedLog.destCountry || 'N/A'}</strong></div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Policy & Traffic Metrics */}
                                            <div className="p-4 rounded-xl bg-[#090E18] border border-white/[0.06] space-y-3">
                                                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                                                    FIREWALL POLICY & TRAFFIC METRICS
                                                </span>
                                                <div className="grid grid-cols-3 gap-3 font-mono text-xs">
                                                    <div>
                                                        <span className="text-slate-500 block text-[10px]">Rule Name:</span>
                                                        <span className="font-bold text-slate-200">{selectedLog.policyname || 'N/A'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 block text-[10px]">Policy ID:</span>
                                                        <span className="font-bold text-slate-200">{selectedLog.policyid || selectedLog.policyId || 'N/A'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 block text-[10px]">Session ID:</span>
                                                        <span className="font-bold text-slate-200">{selectedLog.sessionid || 'N/A'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 block text-[10px]">Sent Data:</span>
                                                        <span className="font-bold text-emerald-400">{formatBytes(selectedLog.sentbyte)} ({selectedLog.sentpkt || 0} pkts)</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 block text-[10px]">Received Data:</span>
                                                        <span className="font-bold text-blue-400">{formatBytes(selectedLog.rcvdbyte)} ({selectedLog.rcvdpkt || 0} pkts)</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 block text-[10px]">Duration:</span>
                                                        <span className="font-bold text-slate-200">{selectedLog.duration ? `${selectedLog.duration}s` : 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Quick Actions Footer inside Drawer */}
                                            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-white/[0.08]">
                                                {srcIp && srcIp !== 'Internal' && (
                                                    <button
                                                        onClick={(e) => {
                                                            if (stagingStatus[selectedLog._id] === 'staged') {
                                                                navigate('/firewall-approvals');
                                                            } else {
                                                                handleStageFirewallBlock(e, selectedLog);
                                                            }
                                                        }}
                                                        disabled={stagingStatus[selectedLog._id] === 'staging'}
                                                        className={`flex-1 py-2.5 px-4 rounded-xl text-white font-mono text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg transition-all ${stagingStatus[selectedLog._id] === 'staged'
                                                            ? 'bg-emerald-600 hover:bg-emerald-500 ring-2 ring-emerald-400/50 cursor-pointer animate-pulse'
                                                            : 'bg-rose-600 hover:bg-rose-500 cursor-pointer'
                                                            }`}
                                                    >
                                                        {stagingStatus[selectedLog._id] === 'staging' ? (
                                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                                        ) : stagingStatus[selectedLog._id] === 'staged' ? (
                                                            <>
                                                                <ExternalLink className="w-4 h-4 text-emerald-200" /> View in Firewall Approvals →
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ShieldAlert className="w-4 h-4" /> Stage Perimeter Block
                                                            </>
                                                        )}
                                                    </button>
                                                )}

                                                <button
                                                    onClick={(e) => handleCreateZohoTicket(e, selectedLog)}
                                                    disabled={zohoStatus[selectedLog._id] === 'loading' || zohoStatus[selectedLog._id] === 'created'}
                                                    className="flex-1 py-2.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-mono text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg transition-all disabled:opacity-50"
                                                >
                                                    {zohoStatus[selectedLog._id] === 'created' ? (
                                                        <>
                                                            <CheckCircle2 className="w-4 h-4" /> Ticket Created
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Ticket className="w-4 h-4" /> Create Zoho Ticket
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {drawerTab === 'attributes' && (
                                    <div className="space-y-2">
                                        <div className="grid grid-cols-1 gap-2">
                                            {Object.entries(selectedLog).map(([key, value]) => (
                                                <div
                                                    key={key}
                                                    className="p-2.5 rounded-lg bg-[#090E18] border border-white/[0.04] flex items-center justify-between font-mono text-xs"
                                                >
                                                    <span className="text-slate-400 font-bold">{key}</span>
                                                    <span className="text-slate-200 truncate max-w-[340px] text-right">
                                                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {drawerTab === 'raw' && (
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[10px] font-mono text-slate-400 uppercase">
                                                FortiOS Syslog JSON Schema
                                            </span>
                                            <button
                                                onClick={() => copyRawPayload(selectedLog)}
                                                className="flex items-center gap-1.5 text-xs font-mono text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20"
                                            >
                                                <Copy className="w-3.5 h-3.5" />
                                                {copiedRaw ? "Copied!" : "Copy JSON"}
                                            </button>
                                        </div>
                                        <pre className="p-4 rounded-xl bg-[#090E18] border border-white/[0.08] text-[11px] font-mono text-emerald-400/90 whitespace-pre-wrap overflow-x-auto leading-relaxed shadow-inner">
                                            {JSON.stringify(selectedLog, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Custom Scrollbar Styles */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 20px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
            ` }} />
        </div>
    );
};

export default EventExplorer;
